import { supabase } from "@/integrations/supabase/client";
import { PaginatedResponse, PaginationParams } from "@/types";
import type { Json } from "@/types/supabase";
import { 
  getUsersByCriteria, 
  countUsersByCriteria, 
  getUsersForPredefinedGroup, 
  countUsersForPredefinedGroup,
  getTotalUniqueMembers,
  refreshAllGroupMemberCounts,
  type GroupMember,
  type GroupCriteria
} from "@/services/userGroupService";

// Helper function to get current user's profile ID
const getCurrentUserProfileId = async (): Promise<number> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('id')
    .eq('uid', user.user.id)
    .single();

  if (profileError || !profile) {
    throw new Error('User profile not found');
  }

  return profile.id;
};

// Helper function to search users for targeting
export const searchUsersForGroup = async (
  query: string,
  limit = 10
): Promise<Array<{ id: number; name: string; email: string; avatar_url: string | null }>> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, email, avatar_url')
      .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
      .eq('is_active', true)
      .limit(limit);

    if (error) {
      console.error("Error searching users:", error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Error in searchUsersForGroup:", error);
    throw error;
  }
};

// Enhanced Types for User Groups with better criteria handling
export interface UserGroup {
  id: number;
  name: string;
  description: string | null;
  criteria: Json; // Dynamic criteria for filtering users (jsonb)
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  member_count: number; // Cached count for performance
  last_refreshed_at: string | null; // When member count was last calculated
}

// New interface for group criteria validation
export interface GroupCriteriaValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface GroupCampaign {
  id: number;
  group_id: number;
  type: "notification" | "promotion" | "offer" | "announcement";
  title: string;
  message: string;
  status: "draft" | "scheduled" | "sending" | "sent" | "failed" | "cancelled";
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  created_by: number;
  
  // Campaign-specific data (promotions, offers, etc.)
  campaign_data: Json | null; // promotion codes, discount amounts, etc.
  
  // Delivery stats
  target_count: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
}

export interface CampaignRecipient {
  id: number;
  campaign_id: number;
  user_id: number; // References profiles.id
  status: "pending" | "sent" | "delivered" | "failed" | "opened" | "clicked";
  sent_at?: string | null;
  delivered_at?: string | null;
  opened_at?: string | null;
  clicked_at?: string | null;
  error_message?: string | null;
  device_info?: Json | null; // push token, device type, etc.
  // Additional fields from notification_deliveries table
  automation_rule_id?: number;
  action_url?: string;
  image_url?: string;
  failed_devices?: number;
  clicked_device_platform?: string;
  created_at?: string;
  user?: {
    name: string;
    email: string;
    avatar_url: string | null;
  };
}

// User Groups CRUD Operations

export const fetchUserGroups = async (
  pagination: PaginationParams = { page: 1, pageSize: 10 },
  filters: {
    search?: string;
    status?: string;
  } = {}
): Promise<PaginatedResponse<UserGroup>> => {
  const { page = 1, pageSize = 10 } = pagination;
  try {
    let query = supabase
      .from("user_groups")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false });

    // Apply filters
    if (filters.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
      );
    }

    if (filters.status) {
      switch (filters.status) {
        case "active":
          query = query.eq("is_active", true);
          break;
        case "inactive":
          query = query.eq("is_active", false);
          break;
      }
    }

    // Apply pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching user groups:", error);
      throw error;
    }

    return {
      data: data || [],
      total: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  } catch (error) {
    console.error("Error in fetchUserGroups:", error);
    throw error;
  }
};

export const fetchUserGroup = async (id: number): Promise<UserGroup | null> => {
  try {
    const { data, error } = await supabase
      .from("user_groups")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching user group:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error in fetchUserGroup:", error);
    throw error;
  }
};

export const createUserGroup = async (
  groupData: Omit<UserGroup, "id" | "created_at" | "updated_at" | "member_count" | "last_refreshed_at" | "created_by">
): Promise<UserGroup> => {
  try {
    // Get current user's profile ID for created_by field
    const currentUserProfileId = await getCurrentUserProfileId();

    const { data, error } = await supabase
      .from("user_groups")
      .insert({
        ...groupData,
        member_count: 0,
        created_by: currentUserProfileId,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating user group:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error in createUserGroup:", error);
    throw error;
  }
};

export const updateUserGroup = async (
  id: number,
  updates: Partial<Omit<UserGroup, "id" | "created_at" | "updated_at" | "created_by">>
): Promise<UserGroup> => {
  try {
    const { data, error } = await supabase
      .from("user_groups")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating user group:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error in updateUserGroup:", error);
    throw error;
  }
};

export const deleteUserGroup = async (id: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from("user_groups")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting user group:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in deleteUserGroup:", error);
    throw error;
  }
};

export const toggleUserGroupStatus = async (
  id: number,
  isActive: boolean
): Promise<UserGroup> => {
  return updateUserGroup(id, { is_active: isActive });
};

// Enhanced Group Member Management using Frontend Logic

export const refreshGroupMemberCount = async (groupId: number): Promise<number> => {
  try {
    // Get group details
    const { data: group, error: groupError } = await supabase
      .from('user_groups')
      .select('name, criteria')
      .eq('id', groupId)
      .single();

    if (groupError || !group) {
      throw new Error('Group not found');
    }

    const predefinedGroups = [
      "Active Book Readers", "Audio Content Consumers", "Multi-Format Consumers",
      "Content Completion Champions", "Engaged Highlighters", "Feedback Providers",
      "Category Enthusiasts", "Content Discovery Users", "Subscription Holders",
      "Free Content Users", "Free Content Users with 30+ Minutes Reading",
      "High-Value Subscribers", "At-Risk Churn Users", "Recent Registrations",
      "Inactive Users", "Engagement Leaders", "Age Group Specific",
      "Referral Users", "Platform Specific Users", "Feature Adopters",
      "High Completion Rate Users", "Free Users - No Plan Ever", "Expired Subscribers",
      "Cancelled Subscribers", "Free Users with Account Age 30+ Days",
      "Free Users with Account Age 90+ Days", "Free Users with 5+ Books Read",
      "Free Users with 60+ Minutes Reading", "Free Users with 10+ Books Read",
      "Free Users with 2+ Hours Reading", "Expired Subscribers with 30+ Days Account Age",
      "Cancelled Subscribers with Reading Activity", "Free Users - High Engagement"
    ];

    const isPredefined = predefinedGroups.includes(group.name);
    const criteria = group.criteria as GroupCriteria;
    
    // Always use TypeScript functions which properly handle latest subscription logic
    // This ensures consistency and correct behavior for all groups
    let memberCount = 0;
    if (isPredefined) {
      memberCount = await countUsersForPredefinedGroup(group.name);
    } else if (criteria) {
      memberCount = await countUsersByCriteria(criteria);
    }

    // Update the group with new count
    const { error: updateError } = await supabase
      .from('user_groups')
      .update({
        member_count: memberCount,
        last_refreshed_at: new Date().toISOString()
      })
      .eq('id', groupId);

    if (updateError) {
      throw updateError;
    }

    return memberCount;
  } catch (error) {
    console.error("Error in refreshGroupMemberCount:", error);
    throw error;
  }
};

export const getGroupMembers = async (
  groupId: number,
  limit?: number
): Promise<Array<{ user_id: number; name: string; email: string; avatar_url: string; created_at: string }>> => {
  try {
    // Get the group details
    const { data: group, error: groupError } = await supabase
      .from('user_groups')
      .select('name, criteria')
      .eq('id', groupId)
      .single();

    if (groupError || !group) {
      throw new Error('Group not found');
    }

    let members: GroupMember[] = [];

    // Check if it's a predefined group
    const predefinedGroups = [
      "Active Book Readers", "Audio Content Consumers", "Multi-Format Consumers",
      "Content Completion Champions", "Engaged Highlighters", "Feedback Providers",
      "Category Enthusiasts", "Content Discovery Users", "Subscription Holders",
      "Free Content Users", "Free Content Users with 30+ Minutes Reading",
      "High-Value Subscribers", "At-Risk Churn Users", "Recent Registrations",
      "Inactive Users", "Engagement Leaders", "Age Group Specific",
      "Referral Users", "Platform Specific Users", "Feature Adopters",
      "High Completion Rate Users"
    ];

    if (predefinedGroups.includes(group.name)) {
      members = await getUsersForPredefinedGroup(group.name);
    } else {
      // Custom group with criteria
      members = await getUsersByCriteria(group.criteria as GroupCriteria);
    }

    // Apply limit only if provided, otherwise return all members
    const limitedMembers = limit !== undefined ? members.slice(0, limit) : members;
    
    return limitedMembers.map(member => ({
      user_id: member.user_id,
      name: member.name,
      email: member.email,
      avatar_url: member.avatar_url || '',
      created_at: member.created_at
    }));
  } catch (error) {
    console.error("Error in getGroupMembers:", error);
    throw error;
  }
};

// New function to get detailed group members with subscription info (optimized with database-level pagination)
export const getDetailedGroupMembers = async (
  groupId: number,
  limit?: number,
  offset = 0
): Promise<Array<{
  user_id: number;
  name: string;
  email: string;
  avatar_url: string;
  created_at: string;
  subscription_status: string;
  subscription_plan_name: string;
  total_reading_seconds?: number;
  books_read_count?: number;
  highlights_count?: number;
  feedback_count?: number;
  audio_plays_count?: number;
  completion_rate?: number;
  account_age_days?: number;
  last_activity_days?: number;
}>> => {
  try {
    // Get the group details
    const { data: group, error: groupError } = await supabase
      .from('user_groups')
      .select('name, criteria')
      .eq('id', groupId)
      .single();

    if (groupError || !group) {
      throw new Error('Group not found');
    }

    // Check if it's a predefined group
    const predefinedGroups = [
      "Active Book Readers", "Audio Content Consumers", "Multi-Format Consumers",
      "Content Completion Champions", "Engaged Highlighters", "Feedback Providers",
      "Category Enthusiasts", "Content Discovery Users", "Subscription Holders",
      "Free Content Users", "Free Content Users with 30+ Minutes Reading",
      "High-Value Subscribers", "At-Risk Churn Users", "Recent Registrations",
      "Inactive Users", "Engagement Leaders", "Age Group Specific",
      "Referral Users", "Platform Specific Users", "Feature Adopters",
      "High Completion Rate Users"
    ];

    // For predefined groups, use optimized database queries
    if (predefinedGroups.includes(group.name)) {
      return await getDetailedGroupMembersOptimized(group.name, group.criteria as GroupCriteria, limit, offset);
    } else {
      // For custom groups, use optimized query based on criteria
      return await getDetailedGroupMembersOptimized(null, group.criteria as GroupCriteria, limit, offset);
    }
  } catch (error) {
    console.error("Error in getDetailedGroupMembers:", error);
    throw error;
  }
};

// Optimized function that does database-level filtering, sorting, and pagination
const getDetailedGroupMembersOptimized = async (
  groupName: string | null,
  criteria: GroupCriteria,
  limit?: number,
  offset = 0
): Promise<Array<{
  user_id: number;
  name: string;
  email: string;
  avatar_url: string;
  created_at: string;
  subscription_status: string;
  subscription_plan_name: string;
  total_reading_seconds?: number;
  books_read_count?: number;
  highlights_count?: number;
  feedback_count?: number;
  audio_plays_count?: number;
  completion_rate?: number;
  account_age_days?: number;
  last_activity_days?: number;
}>> => {
  try {
    const pageLimit = limit || 10;
    
    // For "Free Content Users", use an optimized query with database-level filtering
    if (criteria.subscription_status === 'free') {
      // Get truly active subscription user IDs based on LATEST subscription
      // Users with expired, cancelled, or past-end subscriptions should be considered "free"
      const { data: allSubscriptions } = await supabase
        .from('user_subscriptions')
        .select('user_id, status, subscription_end, created_at')
        .order('created_at', { ascending: false });
      
      // Group by user_id and get the most recent subscription for each user
      const latestSubscriptions = new Map<number, { status: string; subscription_end: string | null; created_at: string }>();
      allSubscriptions?.forEach(sub => {
        const existing = latestSubscriptions.get(sub.user_id);
        if (!existing || new Date(sub.created_at) > new Date(existing.created_at)) {
          latestSubscriptions.set(sub.user_id, {
            status: sub.status,
            subscription_end: sub.subscription_end,
            created_at: sub.created_at
          });
        }
      });
      
      // Filter to only truly active subscriptions (status='active' AND subscription_end > NOW())
      // Note: subscription_end must be > NOW(), NULL values are excluded
      const now = new Date();
      const trulyActiveUserIds = new Set(
        Array.from(latestSubscriptions.entries())
          .filter(([_, sub]) => {
            // Must be active status
            if (sub.status !== 'active') return false;
            // Include only if subscription_end exists AND is in the future
            return sub.subscription_end && new Date(sub.subscription_end) > now;
          })
          .map(([userId, _]) => userId)
      );
      
      // If total_reading_minutes filter is specified, we need to filter by reading time first
      if (criteria.total_reading_minutes) {
        const minSeconds = (criteria.total_reading_minutes as number) * 60;
        
        // Get all free users (without active subscriptions) who meet reading time requirement
        // We'll aggregate reading time per user and filter
        const { data: allFreeProfiles } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url, registered_at')
          .eq('is_active', true);
        
        if (!allFreeProfiles || allFreeProfiles.length === 0) {
          return [];
        }
        
        // Filter out truly active subscribers (expired/cancelled/past-end are considered free)
        const freeProfileIds = allFreeProfiles
          .filter(p => !trulyActiveUserIds.has(p.id))
          .map(p => p.id);
        
        if (freeProfileIds.length === 0) {
          return [];
        }
        
        // Fetch reading time for all free users and aggregate
        const { data: readingData } = await supabase
          .from('book_reads')
          .select('user_id, active_seconds')
          .in('user_id', freeProfileIds);
        
        // Aggregate total reading time per user
        const readingTimeMap = new Map<number, number>();
        readingData?.forEach((read: { user_id: number; active_seconds: number | null }) => {
          if (read.user_id && read.active_seconds) {
            const currentTotal = readingTimeMap.get(read.user_id) || 0;
            readingTimeMap.set(read.user_id, currentTotal + read.active_seconds);
          }
        });
        
        // Filter users who meet the reading time requirement
        const qualifiedUserIds = freeProfileIds.filter(userId => {
          const totalSeconds = readingTimeMap.get(userId) || 0;
          return totalSeconds >= minSeconds;
        });
        
        if (qualifiedUserIds.length === 0) {
          return [];
        }
        
        // Fetch profiles for qualified users, ordered by registered_at DESC
        const { data: qualifiedProfiles } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url, registered_at')
          .in('id', qualifiedUserIds)
          .order('registered_at', { ascending: false })
          .range(offset, offset + pageLimit - 1);
        
        if (!qualifiedProfiles || qualifiedProfiles.length === 0) {
          return [];
        }
        
        const resultUserIds = qualifiedProfiles.map(p => p.id);
        
        // Fetch reading time for the paginated results
        const { data: paginatedReadingData } = await supabase
          .from('book_reads')
          .select('user_id, active_seconds')
          .in('user_id', resultUserIds);
        
        const paginatedReadingTimeMap = new Map<number, number>();
        paginatedReadingData?.forEach((read: { user_id: number; active_seconds: number | null }) => {
          if (read.user_id && read.active_seconds) {
            const currentTotal = paginatedReadingTimeMap.get(read.user_id) || 0;
            paginatedReadingTimeMap.set(read.user_id, currentTotal + read.active_seconds);
          }
        });
        
        // Format results
        return qualifiedProfiles.map((profile: { id: number; name: string; email: string; avatar_url: string | null; registered_at: string }) => ({
          user_id: profile.id,
          name: profile.name,
          email: profile.email,
          avatar_url: profile.avatar_url || '',
          created_at: profile.registered_at,
          subscription_status: 'free',
          subscription_plan_name: 'No Plan',
          total_reading_seconds: paginatedReadingTimeMap.get(profile.id) || 0,
          books_read_count: undefined,
          highlights_count: undefined,
          feedback_count: undefined,
          audio_plays_count: undefined,
          completion_rate: undefined,
          account_age_days: undefined,
          last_activity_days: undefined
        }));
      }
      
      // No reading time filter - use the simpler approach
      const fetchLimit = Math.min(pageLimit * 5, 100);
      const query = supabase
        .from('profiles')
        .select(`
          id,
          name,
          email,
          avatar_url,
          registered_at
        `)
        .eq('is_active', true)
        .order('registered_at', { ascending: false })
        .range(offset, offset + fetchLimit - 1);
      
      const { data: profiles, error } = await query;
      
      if (error) {
        console.error("Error fetching free users:", error);
        throw error;
      }
      
      if (!profiles || profiles.length === 0) {
        return [];
      }
      
      // Filter out truly active subscribers (expired/cancelled/past-end are considered free)
      const freeUsers = profiles
        .filter(p => !trulyActiveUserIds.has(p.id))
        .slice(0, pageLimit);
      
      if (freeUsers.length === 0) {
        return [];
      }
      
      const userIds = freeUsers.map(p => p.id);
      
      // Fetch total reading time (sum of active_seconds) for these users
      const readingTimeMap = new Map<number, number>();
      
      if (userIds.length > 0) {
        const { data: readingData, error: readingError } = await supabase
          .from('book_reads')
          .select('user_id, active_seconds')
          .in('user_id', userIds);
        
        if (readingError) {
          console.warn("Error fetching reading time:", readingError);
        } else {
          readingData?.forEach((read: { user_id: number; active_seconds: number | null }) => {
            if (read.user_id && read.active_seconds) {
              const currentTotal = readingTimeMap.get(read.user_id) || 0;
              readingTimeMap.set(read.user_id, currentTotal + read.active_seconds);
            }
          });
        }
      }
      
      // Format results
      return freeUsers.map((profile: { id: number; name: string; email: string; avatar_url: string | null; registered_at: string }) => ({
        user_id: profile.id,
        name: profile.name,
        email: profile.email,
        avatar_url: profile.avatar_url || '',
        created_at: profile.registered_at,
        subscription_status: 'free',
        subscription_plan_name: 'No Plan',
        total_reading_seconds: readingTimeMap.get(profile.id) || 0,
        books_read_count: undefined,
        highlights_count: undefined,
        feedback_count: undefined,
        audio_plays_count: undefined,
        completion_rate: undefined,
        account_age_days: undefined,
        last_activity_days: undefined
      }));
    }
    
    // For other criteria, use the existing approach but with database-level pagination
    // This is a fallback - ideally all groups should be optimized
    const members = await getUsersByCriteria(criteria);
    
    // Sort and paginate
    members.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA;
    });
    
    const paginatedMembers = limit !== undefined 
      ? members.slice(offset, offset + limit)
      : members.slice(offset);
    
    return paginatedMembers.map(member => ({
      user_id: member.user_id,
      name: member.name,
      email: member.email,
      avatar_url: member.avatar_url || '',
      created_at: member.created_at,
      subscription_status: member.subscription_status,
      subscription_plan_name: member.subscription_plan_name,
      total_reading_seconds: member.total_reading_seconds,
      books_read_count: member.books_read_count,
      highlights_count: member.highlights_count,
      feedback_count: member.feedback_count,
      audio_plays_count: member.audio_plays_count,
      completion_rate: member.completion_rate,
      account_age_days: member.account_age_days,
      last_activity_days: member.last_activity_days
    }));
  } catch (error) {
    console.error("Error in getDetailedGroupMembersOptimized:", error);
    throw error;
  }
};

// New function to validate group criteria
export const validateGroupCriteria = async (criteria: Json): Promise<GroupCriteriaValidation> => {
  const validation: GroupCriteriaValidation = {
    isValid: true,
    errors: [],
    warnings: []
  };

  try {
    // Check if criteria is empty
    if (!criteria || Object.keys(criteria).length === 0) {
      validation.warnings.push("No specific criteria defined. Group will include all active users.");
      return validation;
    }

    // Validate specific criteria types
    for (const [key, value] of Object.entries(criteria)) {
      switch (key) {
        case 'book_reads_count':
        case 'audio_plays_count':
        case 'highlights_count':
        case 'feedback_count':
        case 'selected_categories_count':
        case 'book_views_count':
          if (typeof value !== 'number' || value < 0) {
            validation.errors.push(`${key} must be a positive number`);
            validation.isValid = false;
          }
          break;
          
        case 'completion_rate':
          if (typeof value !== 'number' || value < 0 || value > 100) {
            validation.errors.push(`${key} must be a number between 0 and 100`);
            validation.isValid = false;
          }
          break;
          
        case 'subscription_status': {
          const validStatuses = ['active', 'free', 'cancelled', 'trial'];
          if (!validStatuses.includes(value as string)) {
            validation.errors.push(`${key} must be one of: ${validStatuses.join(', ')}`);
            validation.isValid = false;
          }
          break;
        }
          
        case 'last_activity_days':
        case 'registration_days':
          if (typeof value !== 'number' || value < 0) {
            validation.errors.push(`${key} must be a positive number`);
            validation.isValid = false;
          }
          break;
      }
    }

    return validation;
  } catch (error) {
    validation.errors.push("Failed to validate criteria");
    validation.isValid = false;
    return validation;
  }
};

// Campaign Management

export const fetchGroupCampaigns = async (
  groupId: number,
  pagination: PaginationParams = { page: 1, pageSize: 10 }
): Promise<PaginatedResponse<GroupCampaign>> => {
  const { page = 1, pageSize = 10 } = pagination;
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from("group_campaigns")
      .select("*", { count: "exact" })
      .eq("group_id", groupId)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("Error fetching group campaigns:", error);
      throw error;
    }

    return {
      data: (data || []).map(item => ({
        ...item,
        type: item.type as "notification" | "promotion" | "offer" | "announcement",
        status: item.status as "draft" | "scheduled" | "sending" | "sent" | "failed" | "cancelled"
      })),
      total: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  } catch (error) {
    console.error("Error in fetchGroupCampaigns:", error);
    throw error;
  }
};

export const createGroupCampaign = async (
  campaignData: Omit<
    GroupCampaign,
    "id" | "created_at" | "created_by" | "target_count" | "sent_count" | "delivered_count" | "opened_count" | "clicked_count"
  >
): Promise<GroupCampaign> => {
  try {
    // Get current user's profile ID for created_by field
    const currentUserProfileId = await getCurrentUserProfileId();

    const { data, error } = await supabase
      .from("group_campaigns")
      .insert({
        ...campaignData,
        target_count: 0,
        sent_count: 0,
        delivered_count: 0,
        opened_count: 0,
        clicked_count: 0,
        created_by: currentUserProfileId,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating group campaign:", error);
      throw error;
    }

    return {
      ...data,
      type: data.type as "notification" | "promotion" | "offer" | "announcement",
      status: data.status as "draft" | "scheduled" | "sending" | "sent" | "failed" | "cancelled"
    };
  } catch (error) {
    console.error("Error in createGroupCampaign:", error);
    throw error;
  }
};

export const updateGroupCampaign = async (
  id: number,
  updates: Partial<Omit<GroupCampaign, "id" | "created_at" | "created_by">>
): Promise<GroupCampaign> => {
  try {
    const { data, error } = await supabase
      .from("group_campaigns")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating group campaign:", error);
      throw error;
    }

    return {
      ...data,
      type: data.type as "notification" | "promotion" | "offer" | "announcement",
      status: data.status as "draft" | "scheduled" | "sending" | "sent" | "failed" | "cancelled"
    };
  } catch (error) {
    console.error("Error in updateGroupCampaign:", error);
    throw error;
  }
};

export const deleteGroupCampaign = async (id: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from("group_campaigns")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting group campaign:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in deleteGroupCampaign:", error);
    throw error;
  }
};

// Campaign Recipients

export const fetchCampaignRecipients = async (
  campaignId: number,
  pagination: PaginationParams = { page: 1, pageSize: 10 }
): Promise<PaginatedResponse<CampaignRecipient>> => {
  const { page = 1, pageSize = 10 } = pagination;
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from("notification_deliveries")
      .select(`
        *,
        user:profiles!notification_deliveries_user_id_fkey(name, email, avatar_url)
      `, { count: "exact" })
      .eq("campaign_id", campaignId)
      .order("sent_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("Error fetching campaign recipients:", error);
      throw error;
    }

    return {
      data: (data || []).map(item => ({
        ...item,
        status: item.status as "pending" | "sent" | "delivered" | "failed" | "opened" | "clicked"
      })),
      total: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  } catch (error) {
    console.error("Error in fetchCampaignRecipients:", error);
    throw error;
  }
};

// Analytics and Stats

export const fetchUserGroupsAnalytics = async (): Promise<{
  total: number;
  active: number;
  inactive: number;
  total_campaigns: number;
  total_recipients: number;
  total_unique_members: number;
}> => {
  try {
    const [groupsResult, campaignsResult, recipientsResult, uniqueMembersResult] = await Promise.all([
      supabase.from("user_groups").select("is_active"),
      supabase
        .from("group_campaigns")
        .select("*", { count: "exact", head: true }),
      supabase
        .from("notification_deliveries")
        .select("*", { count: "exact", head: true }),
      getTotalUniqueMembers(),
    ]);

    if (groupsResult.error) {
      throw groupsResult.error;
    }

    const groups = groupsResult.data || [];
    const analytics = {
      total: groups.length,
      active: groups.filter((g) => g.is_active).length,
      inactive: groups.filter((g) => !g.is_active).length,
      total_campaigns: campaignsResult.count || 0,
      total_recipients: recipientsResult.count || 0,
      total_unique_members: uniqueMembersResult || 0,
    };

    return analytics;
  } catch (error) {
    console.error("Error in fetchUserGroupsAnalytics:", error);
    throw error;
  }
};

// Re-export the function from service
export { getTotalUniqueMembers };

// Re-export the function from service
export { refreshAllGroupMemberCounts };

// New function to get group statistics
export const getUserGroupStats = async (): Promise<{
  totalGroups: number;
  activeGroups: number;
  totalMembers: number;
  groupsWithMembers: number;
  lastRefreshed: string | null;
}> => {
  try {
    const { data: groups, error } = await supabase
      .from("user_groups")
      .select("is_active, member_count, last_refreshed_at");

    if (error) throw error;

    const totalGroups = groups?.length || 0;
    const activeGroups = groups?.filter(g => g.is_active).length || 0;
    const totalMembers = groups?.reduce((sum, g) => sum + (g.member_count || 0), 0) || 0;
    const groupsWithMembers = groups?.filter(g => (g.member_count || 0) > 0).length || 0;
    const lastRefreshed = groups?.reduce((latest, g) => {
      if (!g.last_refreshed_at) return latest;
      if (!latest) return g.last_refreshed_at;
      return new Date(g.last_refreshed_at) > new Date(latest) ? g.last_refreshed_at : latest;
    }, null as string | null) || null;

    return {
      totalGroups,
      activeGroups,
      totalMembers,
      groupsWithMembers,
      lastRefreshed
    };
  } catch (error) {
    console.error("Error getting user group stats:", error);
    throw error;
  }
};